import { useMemo } from "react";
import { CheckCircle, Filter } from "react-feather";
import {
  selectQuickRecipes,
  selectRecipeViewMode,
  selectLastCookedByRecipe,
  selectCookCountByRecipe,
  selectRecipeSort,
  setRecipeViewMode,
  setRecipeSort,
  sortRecipes,
  isCookedOnlySort,
  THIRTY_MINUTES,
  useT,
  type RecipeSortOption,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";
import { RecipeFilterBar, useRecipeFilter } from "../components/RecipeBarFilter";
import { RecipeGrid } from "../components/RecipeGrid";
import { RecipeList } from "../components/RecipeList";
import { RecipeSort } from "../components/RecipeSort";
import { RecipeViewSwitcher } from "../components/RecipeViewSwitcher";

// A centred icon + heading + description empty state — the web equivalent of the
// RN <Empty /> component used on this screen.
function EmptyState({
  icon: Icon,
  heading,
  description,
}: {
  icon: typeof CheckCircle;
  heading: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center text-center py-16">
      <Icon className="w-8 h-8 text-ink-faint mb-3" />
      <p className="text-base font-semibold text-ink">{heading}</p>
      <p className="text-sm text-ink-soft mt-1 max-w-sm">{description}</p>
    </div>
  );
}

// "See all" for the home Quick Recipes rail — every recipe that cooks in 30
// minutes or less.
export default function QuickRecipes() {
  const t = useT();
  const dispatch = useAppDispatch();
  const selectQuick = useMemo(() => selectQuickRecipes(THIRTY_MINUTES), []);
  const recipes = useAppSelector(selectQuick);
  const viewMode = useAppSelector(selectRecipeViewMode);
  const lastCookedAt = useAppSelector(selectLastCookedByRecipe);
  const cookCounts = useAppSelector(selectCookCountByRecipe);
  // Shared sort preference (library/favourites/quick), persisted per device.
  const sort = useAppSelector(selectRecipeSort) as RecipeSortOption;
  const setSort = (option: RecipeSortOption) => dispatch(setRecipeSort(option));

  // Filter state + filtered list (owned by the hook; resets on unmount).
  const { filters, activeKeys, toggle, clear, filteredRecipes } =
    useRecipeFilter(recipes);

  // The list actually shown: filter pills first, then sort (which can also
  // filter, e.g. "Most recently cooked" keeps only cooked recipes) — drives
  // both the content and the empty states.
  const sorted = useMemo(
    () => sortRecipes(filteredRecipes, sort, lastCookedAt, cookCounts),
    [filteredRecipes, sort, lastCookedAt, cookCounts],
  );

  const emptyMessage = t("quickRecipes.emptyMessage");

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold text-ink">
            {t("home.quickRecipes")}
          </h1>
          <p className="text-sm text-ink-soft mt-0.5">
            {t("quickRecipes.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RecipeSort value={sort} onChange={setSort} />
          <RecipeViewSwitcher
            viewMode={viewMode}
            onSwitch={(mode) => dispatch(setRecipeViewMode(mode))}
          />
        </div>
      </div>

      <RecipeFilterBar
        filters={filters}
        activeKeys={activeKeys}
        onToggle={toggle}
        onClear={clear}
        totalCount={recipes.length}
      />

      {isCookedOnlySort(sort) && sorted.length === 0 ? (
        <EmptyState
          icon={CheckCircle}
          heading={t("library.noCookedYet")}
          description={t("quickRecipes.noCookedDescription")}
        />
      ) : activeKeys.length > 0 && filteredRecipes.length === 0 ? (
        <EmptyState
          icon={Filter}
          heading={t("library.noMatches")}
          description={t("quickRecipes.noMatchFilters")}
        />
      ) : viewMode === "grid" ? (
        <RecipeGrid recipes={sorted} emptyMessage={emptyMessage} />
      ) : (
        <RecipeList recipes={sorted} emptyMessage={emptyMessage} />
      )}
    </div>
  );
}
